// File: @/lib/logger.ts
/* eslint-disable no-console, no-restricted-properties */
// Logger service is the ONLY file allowed to use console methods directly

/**
 * Structured logger.
 *
 * Output by environment:
 *   Development (browser + server)  all levels, human-readable
 *   Production server                warn + error only, one JSON line each,
 *                                    so Vercel runtime logs capture them
 *   Production browser               silent (keeps visitors' consoles clean)
 */

// ===================================
// TYPES
// ===================================

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  readonly [key: string]: unknown;
}

// ===================================
// HELPERS
// ===================================

const IS_PRODUCTION = process.env.NODE_ENV === 'production';

function isServer(): boolean {
  return typeof window === 'undefined';
}

function serializeError(error: unknown): LogContext | string {
  if (error instanceof Error) {
    return { name: error.name, message: error.message, stack: error.stack };
  }

  return String(error);
}

function writeProductionServerLog(level: LogLevel, message: string, context?: LogContext): void {
  const line = JSON.stringify({
    level,
    message,
    timestamp: new Date().toISOString(),
    ...context,
  });

  if (level === 'error') {
    console.error(line);
    return;
  }

  console.warn(line);
}

function writeDevelopmentLog(level: LogLevel, message: string, context?: LogContext): void {
  const logMethod = level === 'error' ? console.error : console.log;
  const contextStr = context ? `\n  ${JSON.stringify(context, null, 2)}` : '';
  logMethod(`[${level.toUpperCase()}] ${message}${contextStr}`);
}

function log(level: LogLevel, message: string, context?: LogContext): void {
  if (!IS_PRODUCTION) {
    writeDevelopmentLog(level, message, context);
    return;
  }

  const isActionable = level === 'warn' || level === 'error';
  if (isServer() && isActionable) {
    writeProductionServerLog(level, message, context);
  }
}

// ===================================
// PUBLIC API
// ===================================

/**
 * Global logger instance
 *
 * @example
 * ```typescript
 * import { logger } from '@/lib/logger';
 *
 * logger.info('[Component] ✅ Action completed', { contextData });
 * logger.error('[Component] ❌ Action failed', error, { contextData });
 * ```
 */
export const logger = {
  /** Development only. */
  debug(message: string, context?: LogContext): void {
    if (!IS_PRODUCTION) {
      log('debug', message, context);
    }
  },

  /** Development only (silent in production). */
  info(message: string, context?: LogContext): void {
    log('info', message, context);
  },

  /** Potential issues; reaches production server logs. */
  warn(message: string, context?: LogContext): void {
    log('warn', message, context);
  },

  /** Actual errors with stack traces; reaches production server logs. */
  error(message: string, error: unknown, context?: LogContext): void {
    log('error', message, { ...context, error: serializeError(error) });
  },
} as const;
